import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrg } from '../context/OrgContext';
import { getOrgPendingInvites, revokeInvite, type Invite } from '../services/invite.service';
import { Spinner, toastError, toastSuccess } from '../components/core';

export function SuperAdminOrgInvites() {
  const { i18n } = useTranslation();
  const { orgId } = useOrg();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<Record<string, boolean>>({});

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const fetchedInvites = await getOrgPendingInvites(orgId);
        setInvites(fetchedInvites);
      } catch {
        setError('Failed to load invites');
        toastError('Failed to load invites');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [orgId]);

  async function handleRevoke(token: string) {
    setRevoking(prev => ({ ...prev, [token]: true }));
    try {
      await revokeInvite(token);
      setInvites(prev => prev.filter(inv => inv.token !== token));
      toastSuccess('Invite revoked');
    } catch {
      toastError('Failed to revoke invite');
    } finally {
      setRevoking(prev => { const next = { ...prev }; delete next[token]; return next; });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (invites.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-6 py-8 text-center">
        <p className="text-sm text-gray-500">No pending invites</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-6 py-3 text-start font-body text-xs font-semibold uppercase tracking-wider text-gray-600">
              Email
            </th>
            <th className="px-6 py-3 text-start font-body text-xs font-semibold uppercase tracking-wider text-gray-600">
              Created
            </th>
            <th className="px-6 py-3 text-start font-body text-xs font-semibold uppercase tracking-wider text-gray-600">
              Expires
            </th>
            <th className="px-6 py-3 text-end font-body text-xs font-semibold uppercase tracking-wider text-gray-600">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {invites.map((invite, index) => (
            <tr
              key={invite.token}
              className={index !== invites.length - 1 ? 'border-b border-gray-200' : ''}
            >
              <td className="px-6 py-4 font-body text-sm text-gray-900">
                {invite.email}
              </td>
              <td className="px-6 py-4 font-body text-sm text-gray-700">
                {invite.createdAt.toDate().toLocaleDateString(i18n.language)}
              </td>
              <td className="px-6 py-4 font-body text-sm text-gray-700">
                {invite.expiresAt.toDate().toLocaleDateString(i18n.language)}
              </td>
              <td className="px-6 py-4 text-end">
                <button
                  onClick={() => handleRevoke(invite.token)}
                  disabled={revoking[invite.token] ?? false}
                  className="rounded px-3 py-1.5 font-body text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {revoking[invite.token] ? 'Revoking...' : 'Revoke'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
